import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService, LoginCredentials, LoginResponse } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';

export function useAuth() {
  const navigate = useNavigate();
  const { setAuth, clearAuth, isAuthenticated } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials & { remember?: boolean }) => {
      const response = await authService.login(credentials);
      
      // Lexik JWT returns token and refresh_token, but not user data
      // We need to decode the token to get user info
      if (!response.user) {
        try {
          // Decode JWT token payload
          const tokenParts = response.token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            response.user = {
              id: payload.id || 0,
              email: payload.email || credentials.email,
              fullName: payload.fullName || '',
              roles: payload.roles || [],
              isActive: payload.isActive !== undefined ? payload.isActive : true,
            };
          } else {
            throw new Error('Invalid token format');
          }
        } catch (err) {
          // Fallback if token decode fails
          response.user = {
            id: 0,
            email: credentials.email,
            fullName: '',
            roles: [],
            isActive: true,
          };
        }
      }
      
      // Ensure user is defined
      if (!response.user) {
        throw new Error('Unable to extract user information from token');
      }
      
      // Check if user is active
      if (response.user.isActive === false) {
        // Clear any tokens that might have been set
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        throw new Error('Votre compte utilisateur est désactivé. Veuillez contacter un administrateur pour réactiver votre compte.');
      }
      
      return response;
    },
    onSuccess: (data: LoginResponse) => {
      // Validate token before storing
      if (!data.token) {
        throw new Error('No token received from server');
      }

      // Update store (which handles secure storage)
      setAuth(data.token, data.refresh_token || null, data.user || null);

      // Use setTimeout to ensure store is updated before navigation
      // This prevents ProtectedRoute from checking auth before store is ready
      setTimeout(() => {
        // Redirect based on role
        const userRoles = data.user?.roles || [];
        if (userRoles.includes('ROLE_ROOT') || userRoles.includes('ROLE_SUPER_ROOT')) {
          navigate('/admin/dashboard', { replace: true });
        } else if (userRoles.includes('ROLE_ADMIN')) {
          navigate('/admin/dashboard', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }, 0);
    },
    onError: (error) => {
      console.error('Login error:', error);
      // Don't redirect on login error - let the component handle it
      // The error will be available in the mutation error state
    },
  });

  const logout = useCallback(() => {
    clearAuth();
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    navigate('/login');
  }, [clearAuth, navigate]);

  return {
    login: loginMutation.mutate,
    isLoading: loginMutation.isPending,
    error: loginMutation.error,
    isAuthenticated: isAuthenticated(),
    logout,
  };
}
